
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay } from "date-fns";
import { toast } from "sonner";

// Limit for incrementing copy counts per prompt per user per day
const MAX_DAILY_COPY_INCREMENT_PER_PROMPT = 10;

/**
 * Records a copy of a prompt in the database
 */
export const recordPromptCopy = async (
  promptId: string,
  userId: string,
  shouldCountInTotal: boolean
): Promise<boolean> => {
  try {
    console.log(`Recording prompt copy: prompt=${promptId}, user=${userId}, countInTotal=${shouldCountInTotal}`);
    
    const { data, error } = await supabase
      .from('prompt_copies')
      .insert({
        prompt_id: promptId,
        user_id: userId,
        counted_in_total: shouldCountInTotal
      })
      .select('id');
      
    if (error) {
      console.error('Error recording prompt copy:', error);
      return false;
    }
    
    console.log('Prompt copy recorded successfully with ID:', data?.[0]?.id);
    return true;
  } catch (err) {
    console.error('Error in recordPromptCopy:', err);
    return false;
  }
};

/**
 * Check if a user has reached their copy increment limit for a specific prompt today
 */
export const hasReachedPromptDailyCopyLimit = async (userId: string, promptId: string): Promise<boolean> => {
  if (!userId || !promptId) return false;
  
  try {
    const today = new Date();
    const startOfToday = startOfDay(today).toISOString();
    const endOfToday = endOfDay(today).toISOString();
    
    console.log(`Checking daily copy limit for prompt=${promptId}, user=${userId}, period=${startOfToday} to ${endOfToday}`);
    
    // Count how many copies have been marked as counted_in_total for this prompt, user, and day
    const { count, error } = await supabase
      .from('prompt_copies')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('prompt_id', promptId)
      .eq('counted_in_total', true)
      .gte('created_at', startOfToday)
      .lte('created_at', endOfToday);
    
    if (error) {
      console.error('Error checking prompt daily copy limit:', error);
      return false;
    }
    
    console.log(`Daily copy count for user ${userId}, prompt ${promptId}: ${count}/${MAX_DAILY_COPY_INCREMENT_PER_PROMPT}`);
    
    return count !== null && count >= MAX_DAILY_COPY_INCREMENT_PER_PROMPT;
  } catch (err) {
    console.error('Error in hasReachedPromptDailyCopyLimit:', err);
    return false;
  }
};
